import React from 'react';

import styled from 'styled-components';

import { SCORE_SIGN } from './constants';
import ScoreBar from './ScoreBar';

type Props = {
  score: number;
  average?: number | null;
  isSmall?: boolean;
  forceNegative?: boolean;
  showAverageStatus?: boolean;
  vsAvgLabel?: string;
};

const Container = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;

  .row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    color: var(--text-secondary);
  }

  .group {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
  }

  .score {
    font-family: var(--font-regular);
    font-size: 40px;
    line-height: 1;
    color: var(--text-primary);
  }

  .suffix,
  .vs-avg {
    font-size: 16px;
    color: var(--text-primary);
  }

  .delta,
  .avg-status {
    font-size: 16px;

    &.positive {
      color: var(--positive-green);
    }
    &.negative {
      color: var(--inactive-orange);
    }
  }

  .bar-section {
    position: relative;
    margin-top: 6px;
    padding-bottom: 18px;
  }

  .indicator {
    position: absolute;
    top: 50%;
    width: 1px;
    height: 12px;
    background-color: var(--text-primary);
    transform: translate(-50%, -50%);
  }

  .avg-label {
    position: absolute;
    bottom: 0;
    transform: translateX(-50%);
    font-size: 12px;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  &.compact {
    .row {
      justify-content: space-between;
    }
    .score {
      font-size: 16px;
    }
    .suffix,
    .delta,
    .avg-status,
    .vs-avg {
      font-size: 12px;
    }
    .bar-section {
      padding-bottom: 0;
    }
  }
`;

const limitPercentage = (n: number): number => Math.max(0, Math.min(100, n));

const ScorePill = ({
  score,
  average,
  isSmall = false,
  forceNegative = false,
  showAverageStatus = false,
  vsAvgLabel = 'vs avg',
}: Props) => {
  const hasAvg = typeof average === 'number';
  const delta = hasAvg ? Math.round(score - average) : 0;
  const isAboveAvg = !hasAvg || delta >= 0;
  const sign = isAboveAvg ? SCORE_SIGN.POSITIVE : SCORE_SIGN.NEGATIVE;
  const barSign = forceNegative ? SCORE_SIGN.NEGATIVE : sign;

  return (
    <Container className={isSmall ? 'compact' : ''}>
      <div className="row">
        <span className="group">
          <span className="score">{score}</span>
          <span className="suffix">/100</span>
        </span>
        {hasAvg && !showAverageStatus && (
          <span className="group">
            <span className={`delta ${sign}`}>{delta >= 0 ? `+${delta}` : delta}</span>
            <span className="vs-avg">
              {vsAvgLabel}
              {isSmall ? ` ${average}` : ''}
            </span>
          </span>
        )}
        {hasAvg && showAverageStatus && (
          <span className={`avg-status ${sign}`}>{isAboveAvg ? 'Above average' : 'Below average'}</span>
        )}
      </div>
      <div className="bar-section">
        <ScoreBar score={score} positive={barSign === SCORE_SIGN.POSITIVE}>
          {!isSmall && hasAvg && <div className="indicator" style={{ left: `${limitPercentage(average)}%` }} />}
        </ScoreBar>
        {!isSmall && hasAvg && (
          <div className="avg-label" style={{ left: `${limitPercentage(average)}%` }}>
            {average} avg
          </div>
        )}
      </div>
    </Container>
  );
};

export default ScorePill;
